// Easy buy (Buy a phone → EASY BUY): a sheet of phones bought from a seller whose details are on
// the store's paper form, so nothing about them is typed here. One numbered row per phone with
// just the IMEI, the phone, its storage, what it cost and what it sells for; each row goes to
// Inventory, gets its price sticker printed right away (Sticker), or goes to a wholesale buyer
// (Wholesale). The phones count in Phones bought with what each cost, like any purchase.
import { useMemo, useRef, useState } from 'react';
import { get, post } from '../lib/api';
import { navigate, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents } from '../../shared/money';
import { BUY_PAYOUTS, PAYOUT_LABELS, isAppPayout, type Payout, type WholesalePay } from '../../shared/buyback';
import { Badge, Button, Cost, Field, MoneyInput, Profit, Spinner, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { PAY_NOTE } from './Buyback';
import { codeProblem, codesFromText } from './BuyMany';
import { printPriceStickers, type StickerPhone } from './PriceSticker';
import { WholesalePayPicker } from './Wholesale';

interface Catalog { brands: Record<string, Record<string, string[]>>; carriers: string[]; conditions: string[]; grade_help?: Record<string, string> }
type Go = 'inventory' | 'sticker' | 'wholesale';
interface Row { key: string; code: string; brand: string; model: string; storage: string; cost: number | null; sell: number | null; go: Go }

let seq = 0;
const blank = (code = ''): Row => ({ key: `eb_${++seq}`, code, brand: 'Apple', model: '', storage: '', cost: null, sell: null, go: 'inventory' });
const MAX = 30;

/** The brand a typed model belongs to ("Galaxy S24" is Samsung), from the website's list; Apple for an iPhone or anything unknown. */
function brandOf(model: string, cat: Catalog | null): string {
  const m = model.trim().toLowerCase();
  if (!m) return 'Apple';
  for (const [brand, models] of Object.entries(cat?.brands ?? {})) if (Object.keys(models).some((x) => x.toLowerCase() === m)) return brand;
  if (/galaxy|samsung|\bsm-/.test(m)) return 'Samsung';
  if (/pixel/.test(m)) return 'Google';
  if (/moto/.test(m)) return 'Motorola';
  return 'Apple';
}

export function EasyBuyScreen() {
  const { me, can } = useSession();
  const seesProfit = can('profit.view');
  const toast = useToast();
  const print = usePrint();
  const { run, busy } = useAction();
  const cat = useLoad<Catalog>(() => get('/api/buyback/catalog'), []);
  const buyers = useLoad<{ buyers: string[] }>(() => get('/api/wholesale/buyers'), []);
  const requestId = useRef(`easy_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`);
  const holdDays = Math.max(0, Math.floor(Number(me?.settings?.buyback_hold_days ?? 0) || 0));
  const [rows, setRows] = useState<Row[]>(() => [blank(), blank(), blank(), blank(), blank()]);
  const [pasted, setPasted] = useState('');
  const [payout, setPayout] = useState<Payout>('blue_bag');
  const [payoutRef, setPayoutRef] = useState('');
  const [buyer, setBuyer] = useState('');
  const [wsPaidBy, setWsPaidBy] = useState<WholesalePay | ''>('');
  const [wsPaidRef, setWsPaidRef] = useState('');
  const set = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const allModels = useMemo(() => Object.values(cat.data?.brands ?? {}).flatMap((m) => Object.keys(m)), [cat.data]);
  const storagesFor = (r: Row) => cat.data?.brands?.[r.brand]?.[r.model] ?? [];
  // The grade isn't on the sheet: every phone is a plain used phone ("B" when the website has it).
  const grade = useMemo(() => { const g = cat.data?.conditions ?? []; return g.find((x) => /^b$/i.test(x)) ?? g.find((x) => /good/i.test(x)) ?? g[0] ?? 'B'; }, [cat.data]);

  // Only the rows with something typed count; empty rows at the bottom are just room.
  const filled = rows.filter((r) => r.code.trim() || r.model.trim() || r.cost || r.sell);
  const codes = filled.map((r) => r.code.trim().toUpperCase());
  const wholesale = filled.filter((r) => r.go === 'wholesale');
  const total = filled.reduce((s, r) => s + (r.cost ?? 0), 0);
  const sellTotal = filled.reduce((s, r) => s + (r.sell ?? 0), 0);
  const wsTotal = wholesale.reduce((s, r) => s + (r.sell ?? 0), 0);
  const missing = [
    !filled.length && 'at least one phone',
    filled.some((r) => codeProblem(r.code)) && 'an IMEI or serial on every row',
    codes.some((c, i) => c && codes.indexOf(c) !== i) && 'no phone twice',
    filled.some((r) => !r.model.trim()) && 'the phone on every row',
    filled.some((r) => !(r.cost && r.cost > 0)) && 'the cost on every row',
    filled.some((r) => !(r.sell && r.sell > 0)) && 'the sell price on every row',
    wholesale.length > 0 && buyer.trim().length < 2 && 'the wholesale buyer',
    wholesale.length > 0 && (!wsPaidBy || (wsPaidBy === 'other' && !wsPaidRef.trim())) && 'how the wholesale buyer paid',
    wholesale.length > 0 && holdDays > 0 && `no wholesale (bought phones are held ${holdDays} day${holdDays === 1 ? '' : 's'} first)`,
    isAppPayout(payout) && !payoutRef.trim() && 'the payment confirmation',
  ].filter(Boolean) as string[];

  const addCodes = () => {
    const list = codesFromText(pasted).filter((c) => !rows.some((r) => r.code.trim().toUpperCase() === c));
    if (!list.length) { toast('No IMEI or serial number found in that list.', 'error'); return; }
    setRows((rs) => {
      const next = rs.map((r) => ({ ...r }));
      const queue = [...list];
      for (const r of next) { if (!r.code.trim() && queue.length) r.code = queue.shift()!; }
      while (queue.length && next.length < MAX) next.push(blank(queue.shift()!));
      return next;
    });
    setPasted('');
    toast(`Added ${list.length} phone${list.length === 1 ? '' : 's'}.`, 'good');
  };
  const addRow = () => setRows((rs) => (rs.length < MAX ? [...rs, blank()] : rs));
  const setCode = (r: Row, v: string) => {
    const code = v.replace(/[^0-9A-Za-z-]/g, '').slice(0, 30);
    set(r.key, { code });
    // Typing on the last row opens the next one, like a sheet.
    if (code && rows[rows.length - 1].key === r.key && rows.length < MAX) addRow();
  };

  const save = async () => {
    if (missing.length) return;
    const body = {
      requestId: requestId.current,
      paperForm: true,
      payoutMethod: payout,
      payoutRef: isAppPayout(payout) ? payoutRef.trim() || null : null,
      wholesaleBuyer: wholesale.length ? buyer.trim() : null,
      wholesalePaidBy: wholesale.length ? wsPaidBy || null : null,
      wholesalePaidRef: wholesale.length ? wsPaidRef.trim() || null : null,
      note: 'Easy buy: the seller is on the paper form.',
      items: filled.map((r) => ({
        brand: r.brand, model: r.model.trim(), storage: r.storage.trim() || null, color: null, carrier: 'Unlocked', locked: false,
        imei: /^\d{15}$/.test(r.code.trim()) ? r.code.trim() : null, serial: /^\d{15}$/.test(r.code.trim()) ? null : r.code.trim().toUpperCase(),
        condition: grade, offerCents: r.cost, destination: r.go === 'wholesale' ? 'wholesale' : 'inventory',
        sellPriceCents: r.go === 'wholesale' ? null : r.sell, wholesalePriceCents: r.go === 'wholesale' ? r.sell : null,
      })),
    };
    const res = await run((approval?: ApprovalInput) => post<{ id: string; phones?: any[] }>('/api/buybacks/batch', { ...body, approval }));
    if (!res) return;
    // The rows marked Sticker print their price stickers now (the phone, its IMEI and the sell price).
    const stickers: StickerPhone[] = filled.filter((r) => r.go === 'sticker').map((r) => ({
      brand: r.brand, model: r.model.trim(), storage: r.storage.trim() || null, color: null, carrier: 'Unlocked', locked: false,
      imei: /^\d{15}$/.test(r.code.trim()) ? r.code.trim() : null, serial: /^\d{15}$/.test(r.code.trim()) ? null : r.code.trim().toUpperCase(),
      battery_health: null, price_cents: r.sell ?? 0,
    }));
    if (stickers.length) printPriceStickers(print, stickers, me?.settings);
    toast(`Bought ${filled.length} phone${filled.length === 1 ? '' : 's'} for ${formatCents(total)}.${stickers.length ? ` ${stickers.length} sticker${stickers.length === 1 ? '' : 's'} printing.` : ''}`, 'good');
    navigate(`/buybacks/batch/${res.id}?saved=1`);
  };

  if (cat.loading && !cat.data && !cat.error) return <div className="page"><Spinner label="Loading" /></div>;
  const amount = formatCents(total);
  return (
    <div className="page" style={{ paddingBottom: 120 }}>
      <div className="page-head">
        <h1>Easy buy</h1>
        <div className="row-wrap">
          <Button variant="ghost" icon="phoneCash" onClick={() => navigate('/buy')}>Buy one phone</Button>
          <Button variant="ghost" icon="boxes" onClick={() => navigate('/buy-many/bulk')}>Full bulk buy</Button>
          <Button variant="ghost" icon="list" onClick={() => navigate('/buybacks')}>Phones bought</Button>
        </div>
      </div>
      <p className="small muted" style={{ margin: 0 }}>No customer info here: the seller's details are on your paper form. One row per phone: the IMEI, the phone, its storage, what it cost and what it sells for. Each row goes to <strong>Inventory</strong>, prints its <strong>Sticker</strong> right away (and goes to inventory on sale), or goes <strong>Wholesale</strong> to a buyer. Every phone counts in Phones bought.</p>
      {!me?.register ? <div className="banner warn"><Icon.alert /><div>Buying phones needs the store tablet.</div></div> : null}

      <section className="card stack">
        <div className="eb-paste">
          <input className="input" value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder="Paste a list of IMEIs here (optional): they fill the rows" aria-label="Paste a list of IMEIs" />
          <Button icon="plus" disabled={!codesFromText(pasted).length} onClick={addCodes}>Add {codesFromText(pasted).length || ''} phone{codesFromText(pasted).length === 1 ? '' : 's'}</Button>
        </div>
        <div className="list eb-scroll">
          <table className="table eb-sheet">
            <thead>
              <tr>
                <th className="eb-n">#</th><th>IMEI or serial</th><th>Phone</th><th>GB</th><th className="right">Cost</th><th className="right">Sell price</th><th>Goes to</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const bad = r.code.trim() ? codeProblem(r.code) : null;
                const up = r.code.trim().toUpperCase();
                const twice = !!up && codes.indexOf(up) !== codes.lastIndexOf(up) && codes.indexOf(up) !== filled.indexOf(r);
                const storages = storagesFor(r);
                return (
                  <tr key={r.key} className={r.code.trim() || r.model.trim() ? 'eb-row-on' : undefined}>
                    <td className="eb-n num">{i + 1}</td>
                    <td>
                      <input className={`input num${bad || twice ? ' input-bad' : ''}`} value={r.code} inputMode="numeric" autoComplete="off" placeholder="15 digits" aria-label={`Row ${i + 1} IMEI or serial`} onChange={(e) => setCode(r, e.target.value)} />
                      {bad || twice ? <div className="tiny error-text">{twice ? 'Already in the list' : bad}</div> : null}
                    </td>
                    <td>
                      <input className="input" list="eb-models" value={r.model} placeholder="e.g. iPhone 13" aria-label={`Row ${i + 1} phone`} onChange={(e) => set(r.key, { model: e.target.value, brand: brandOf(e.target.value, cat.data ?? null), storage: '' })} />
                    </td>
                    <td>
                      {storages.length ? (
                        <select className="select" value={r.storage} aria-label={`Row ${i + 1} storage`} onChange={(e) => set(r.key, { storage: e.target.value })}>
                          <option value="">GB</option>
                          {storages.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                      ) : <input className="input" value={r.storage} placeholder="128GB" aria-label={`Row ${i + 1} storage`} onChange={(e) => set(r.key, { storage: e.target.value })} />}
                    </td>
                    <td className="right"><MoneyInput value={r.cost} onChange={(v) => set(r.key, { cost: v })} ariaLabel={`Row ${i + 1} cost`} /></td>
                    <td className="right">
                      <MoneyInput value={r.sell} onChange={(v) => set(r.key, { sell: v })} ariaLabel={`Row ${i + 1} sell price`} />
                      {seesProfit && r.cost && r.sell ? <div className="pill-row" style={{ justifyContent: 'flex-end' }}><Profit cents={r.sell - r.cost} label={false} /></div> : null}
                    </td>
                    <td>
                      <div className="chips eb-go" role="group" aria-label={`Row ${i + 1} goes to`}>
                        <button type="button" className="chip" aria-pressed={r.go === 'inventory'} onClick={() => set(r.key, { go: 'inventory' })} title="Into inventory, on sale at the sell price">Inventory</button>
                        <button type="button" className="chip" aria-pressed={r.go === 'sticker'} onClick={() => set(r.key, { go: 'sticker' })} title="Into inventory on sale, and its price sticker prints now">Sticker</button>
                        <button type="button" className="chip" aria-pressed={r.go === 'wholesale'} disabled={holdDays > 0} onClick={() => set(r.key, { go: 'wholesale' })} title={holdDays > 0 ? `Bought phones are held ${holdDays} day${holdDays === 1 ? '' : 's'} first` : 'Sold to a wholesale buyer at the sell price'}>Wholesale</button>
                      </div>
                    </td>
                    <td><Button size="sm" variant="ghost" icon="close" aria-label={`Clear row ${i + 1}`} onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((x) => x.key !== r.key) : [blank()]))} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="eb-models">{allModels.map((m) => <option key={m} value={m} />)}</datalist>
        </div>
        <div className="row-wrap">
          <Button icon="plus" disabled={rows.length >= MAX} onClick={addRow}>Add a row</Button>
          <span className="small muted">{filled.length} phone{filled.length === 1 ? '' : 's'} · cost {formatCents(total)} · sells for {formatCents(sellTotal)}{seesProfit && sellTotal && total ? <> <span className="pill-row"><Cost cents={total} /><Profit cents={sellTotal - total} /></span></> : ''}{rows.length >= MAX ? ` · ${MAX} phones at a time` : ''}</span>
        </div>
      </section>

      {wholesale.length ? (
        <section className="card stack">
          <h2>Wholesale buyer</h2>
          <Field label="Who's buying the wholesale phones" hint={`${wholesale.length} phone${wholesale.length === 1 ? '' : 's'} · ${formatCents(wsTotal)}`}>
            <input className="input" list="eb-buyers" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="e.g. Bay Area Phone Wholesale" />
          </Field>
          <datalist id="eb-buyers">{(buyers.data?.buyers ?? []).map((b) => <option key={b} value={b} />)}</datalist>
          <WholesalePayPicker label={`How they paid the ${formatCents(wsTotal)}`} paidBy={wsPaidBy} paidRef={wsPaidRef} onPaidBy={setWsPaidBy} onPaidRef={setWsPaidRef} />
        </section>
      ) : null}

      <section className="card stack">
        <h2>Paid with</h2>
        <div className="chips" aria-label="Pay with">
          {BUY_PAYOUTS.map((v) => <button key={v} type="button" className="chip" aria-pressed={payout === v} onClick={() => setPayout(v)}>{PAYOUT_LABELS[v]}</button>)}
        </div>
        <p className={`small ${payout === 'cash' ? 'strong' : 'muted'}`} style={{ margin: 0 }}>{PAY_NOTE[payout]}</p>
        {isAppPayout(payout) ? (
          <Field label={payout === 'zelle' ? 'Zelle confirmation number' : payout === 'cashapp' ? 'Cash App payment ID' : 'Apple Pay note'}>
            <input className="input" value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} />
          </Field>
        ) : null}
      </section>

      <div className="sticky-actions">
        <span className="grow small muted">
          {missing.length ? `Still needed: ${missing.join(', ')}` : <>{filled.length} phone{filled.length === 1 ? '' : 's'} · <strong className="num" style={{ color: 'var(--navy)', fontSize: 18 }}>{amount}</strong> · {PAYOUT_LABELS[payout]}{wholesale.length ? ` · ${wholesale.length} wholesale for ${formatCents(wsTotal)}` : ''}{filled.some((r) => r.go === 'sticker') ? ` · ${filled.filter((r) => r.go === 'sticker').length} sticker${filled.filter((r) => r.go === 'sticker').length === 1 ? '' : 's'} print` : ''}</>}
        </span>
        <Button size="lg" variant="primary" icon="check" busy={busy} disabled={!!missing.length || !me?.register} onClick={save}>{payout === 'blue_bag' ? `Pay ${amount} from blue bag` : payout === 'cash' ? `Pay ${amount} cash from drawer` : `Save ${amount} ${PAYOUT_LABELS[payout]} payment`}</Button>
      </div>
      {filled.length ? <Badge tone="plain" icon="info">{grade ? `Every phone is graded ${grade}; change a grade on its page after.` : ''}</Badge> : null}
    </div>
  );
}

/** The big EASY BUY button on Buy a phone and Phones bought. */
export function EasyBuyButton({ compact = false }: { compact?: boolean }) {
  return (
    <button type="button" className={`easy-buy-btn ${compact ? 'easy-buy-compact' : ''}`} onClick={() => navigate('/buy-many/easy')} title="Buy several phones from a paper form: IMEI, phone, GB, cost and sell price per row">
      <Icon.boxes />
      <span className="eb-t">EASY BUY</span>
      <span className="eb-s">buy in bulk · no customer info</span>
    </button>
  );
}
